import React, { useMemo, useState } from "react";
import { Box, Collapse, HStack, Icon, Tag, TagLabel, Text } from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import { getOperatorSymbol } from "utils/analysisUtils";
import { epochConverterSecond, epochConverterSecondWithTimeZone } from "utils/dateUtils";

// ── Parsed metadata filter shape ─────────────────────────────────────────────

interface MetadataKGColumnHeader {
  id?: string;
  name?: string;
  column_type?: string;
}

interface MetadataDateRange {
  low_epoch_sec?: string | number;
  high_epoch_sec?: string | number;
}

interface MetadataDateFilter {
  type?: string;
  date_range?: MetadataDateRange;
}

interface MetadataFilter {
  filter_id?: string;
  kg_column_header?: MetadataKGColumnHeader;
  operator?: string;
  values?: string[];
  date_filter?: MetadataDateFilter;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseDateFilterLabel(df: MetadataDateFilter, timeZone?: string): string {
  if (df.date_range?.low_epoch_sec !== undefined && df.date_range?.high_epoch_sec !== undefined) {
    const format = "d-MMM-yyyy";
    const safeTimeZone = timeZone || undefined;
    const low = safeTimeZone
      ? epochConverterSecondWithTimeZone(Number(df.date_range.low_epoch_sec), format, safeTimeZone)
      : epochConverterSecond(Number(df.date_range.low_epoch_sec));
    const high = safeTimeZone
      ? epochConverterSecondWithTimeZone(Number(df.date_range.high_epoch_sec), format, safeTimeZone)
      : epochConverterSecond(Number(df.date_range.high_epoch_sec));
    return low === high ? low : `Between ${low} to ${high}`;
  }
  return "";
}

function parseFilters(metadata: string | undefined): MetadataFilter[] {
  if (!metadata) return [];
  try {
    const parsed = JSON.parse(metadata);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

interface PandasTableFiltersProps {
  /** Raw metadata JSON string from PandasDataFrameProto.metadata */
  metadata: string;
  /** IANA timezone ID used for date range labels — falls back to IST when absent. */
  defaultTimeZone?: string;
}

const PandasTableFilters: React.FC<PandasTableFiltersProps> = ({ metadata, defaultTimeZone }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const filters = useMemo(() => parseFilters(metadata), [metadata]);

  if (filters.length === 0) return null;

  return (
    <Box borderBottom="1px solid" borderColor="gray.200">
      {/* ── Collapsible header — identical style to KGDataFrameFilters ── */}
      <HStack
        px={3}
        py={2}
        cursor="pointer"
        onClick={() => setIsExpanded(v => !v)}
        justifyContent="space-between"
        _hover={{ bg: "gray.100" }}
        transition="background 0.15s"
      >
        <HStack spacing={1.5}>
          <Icon as={AppIcons.filter} boxSize={3} color="purple.600" />
          <Text fontSize="xs" fontWeight="700" color="gray.700" m={0}>
            FILTERS
          </Text>
          <Text fontSize="xs" color="gray.500" m={0}>
            ({filters.length})
          </Text>
        </HStack>
        <Icon as={isExpanded ? AppIcons.chevronUp : AppIcons.chevronDown} boxSize={2.5} color="gray.500" />
      </HStack>

      {/* ── Filter pills ── */}
      <Collapse in={isExpanded} animateOpacity>
        <Box display="flex" flexWrap="wrap" gap={2} px={2} py={1} bg="white">
          {filters.map((filter, i) => (
            <Tag
              key={filter.filter_id ?? i}
              size="sm"
              borderRadius="md"
              variant="solid"
              bg="white"
              color="gray.800"
              borderWidth="1px"
              borderColor="gray.400"
              p={1}
            >
              <TagLabel fontSize="xs" whiteSpace="nowrap" fontFamily="mono">
                <Box as="span" fontWeight="500" color="gray.900">
                  {filter.kg_column_header?.name}
                </Box>
                <Box as="span" fontWeight="600" color="gray.800">
                  {" "}
                  {getOperatorSymbol(filter.operator ?? "")}{" "}
                  {filter.values && filter.values.length > 0
                    ? filter.values.map(v => String(v).trim()).join(", ")
                    : filter.date_filter
                      ? parseDateFilterLabel(filter.date_filter, defaultTimeZone)
                      : ""}
                </Box>
              </TagLabel>
            </Tag>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default PandasTableFilters;
